import { Heading, InputField, SelectDropdown } from "@/common/components";
import { memo, useEffect, useState } from "@/common/hooks";
import { validate } from "@/utils/Validations";
import { checkTwoFields } from "@/utils/Utilities";

const SecurityInformation = ({
  securityInfo,
  setSecurityInfo,
  debounceState,
  debounceErrors,
  errors,
  setErrors,
  touched,
  setTouched,
  labels,
  encryptData = false,
}) => {
  const questions = [
    "What is your mother's middle name?",
    "What is your father's middle name?",
    "In which state was your mother born?",
    "In which city/town was your mother born?",
    "In which state was your father born?",
    "In which city/town was your father born?",
    "What was your first car?",
    "What is your favorite food?",
    "What is your favorite book?",
    "What was the name of your first pet?",
  ];
  const [inputType, setInputType] = useState({
    challengeQuestion1Answer: encryptData ? "password" : "text",
    challengeQuestion1ConfirmAnswer: encryptData ? "password" : "text",
    challengeQuestion2Answer: encryptData ? "password" : "text",
    challengeQuestion2ConfirmAnswer: encryptData ? "password" : "text",
  });

  const onChange = async (value, state) => {
    if (inputType[state] === "password") {
      setSecurityInfo({ ...securityInfo, [state]: "" });
      setInputType({ ...inputType, [state]: "text" });
      debounceErrors(
        { ...errors, [state]: await validate("", [state]) },
        setErrors
      );
    } else {
      debounceState({ ...securityInfo, [state]: value }, setSecurityInfo);
      debounceErrors(
        { ...errors, [state]: await validate(value, [state]) },
        setErrors
      );
    }
  };

  const errorProps = (state) => {
    return {
      value: securityInfo[state],
      error: errors[state],
      touched: touched[state],
      touch: () => setTouched({ ...touched, [state]: true }),
    };
  };

  useEffect(() => {
    debounceErrors(
      {
        ...errors,
        question1AnswerMatch: checkTwoFields(
          securityInfo.challengeQuestion1Answer,
          securityInfo.challengeQuestion1ConfirmAnswer
        )
          ? labels?.answerMustMatch
          : "",
      },
      setErrors
    );
  }, [
    securityInfo.challengeQuestion1Answer,
    securityInfo.challengeQuestion1ConfirmAnswer,
  ]);

  useEffect(() => {
    debounceErrors(
      {
        ...errors,
        question2AnswerMatch: checkTwoFields(
          securityInfo.challengeQuestion2Answer,
          securityInfo.challengeQuestion2ConfirmAnswer
        )
          ? labels?.answerMustMatch
          : "",
      },
      setErrors
    );
  }, [
    securityInfo.challengeQuestion2Answer,
    securityInfo.challengeQuestion2ConfirmAnswer,
  ]);

  return (
    <section className="mb-5">
      <Heading
        text={labels?.securityInfo}
        wrapperClassNames="h4 border-bottom-1 pb-1 mb-3"
        title={false}
      />
      <InputField
        id="operatorId"
        maxLength="50"
        label={labels?.customOperatorId}
        change={(e) => onChange(e, "customUserId")}
        {...errorProps("customUserId")}
      />

      <p className="mt-4">{labels?.chooseQuestions}</p>
      <SelectDropdown
        id="question1"
        ariaLabel="Question One"
        label={labels?.question1}
        dropdownData={questions.filter(
          (e) => e !== securityInfo.challengeQuestion2
        )}
        selectClassName="w-100 py-2"
        select={(e) => {
          setSecurityInfo({
            ...securityInfo,
            challengeQuestion1: e,
          });
        }}
        blur={async (e) => {
          setErrors({
            ...errors,
            challengeQuestion1: await validate(e, "challengeQuestion1"),
          });
        }}
        defaultOption={true}
        {...errorProps("challengeQuestion1")}
      />
      <InputField
        id="answer1"
        maxLength="40"
        type={inputType.challengeQuestion1Answer}
        label={labels?.answer}
        change={(e) => onChange(e, "challengeQuestion1Answer")}
        {...errorProps("challengeQuestion1Answer")}
      />
      <InputField
        id="confirmAnswer1"
        maxLength="40"
        type={inputType.challengeQuestion1ConfirmAnswer}
        label={labels?.confirmAnswer}
        change={(e) => onChange(e, "challengeQuestion1ConfirmAnswer")}
        {...errorProps("challengeQuestion1ConfirmAnswer")}
        error={
          errors.challengeQuestion1ConfirmAnswer || errors.question1AnswerMatch
        }
      />

      <SelectDropdown
        id="question2"
        ariaLabel="Question Two"
        label={labels?.question2}
        dropdownData={questions.filter(
          (e) => e !== securityInfo.challengeQuestion1
        )}
        selectClassName="w-100 py-2"
        select={(e) => {
          setSecurityInfo({
            ...securityInfo,
            challengeQuestion2: e,
          });
        }}
        blur={async (e) => {
          setErrors({
            ...errors,
            challengeQuestion2: await validate(e, "challengeQuestion2"),
          });
        }}
        defaultOption={true}
        {...errorProps("challengeQuestion2")}
      />
      <InputField
        id="answer2"
        maxLength="40"
        type={inputType.challengeQuestion2Answer}
        label={labels?.answer}
        change={(e) => onChange(e, "challengeQuestion2Answer")}
        {...errorProps("challengeQuestion2Answer")}
      />
      <InputField
        id="confirmAnswer2"
        maxLength="40"
        type={inputType.challengeQuestion2ConfirmAnswer}
        label={labels?.confirmAnswer}
        change={(e) => onChange(e, "challengeQuestion2ConfirmAnswer")}
        {...errorProps("challengeQuestion2ConfirmAnswer")}
        error={
          errors.challengeQuestion2ConfirmAnswer || errors.question2AnswerMatch
        }
      />
    </section>
  );
};

export default memo(SecurityInformation);
